import { Trans, useLingui } from '@lingui/react/macro';
import type { FabricEditor } from 'modules/fabric-editor';
import { useEditorDirty, useSaveTemplate, useSaveHotkey } from 'modules/manager/ui/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { history } from 'store/store';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

interface UnsavedChangesGuardProps {
  editor: FabricEditor | null;
}

interface PendingTx {
  retry: () => void;
}

const UnsavedChangesGuard: React.FC<UnsavedChangesGuardProps> = ({ editor }) => {
  const { t } = useLingui();
  const isDirty = useEditorDirty(editor);
  const { save, isSaving } = useSaveTemplate(editor);
  const [pendingTx, setPendingTx] = useState<PendingTx | null>(null);

  useSaveHotkey(editor, save);

  useEffect(() => {
    if (!isDirty) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required for legacy browsers; modern browsers ignore custom strings.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;

    const unblock = history.block((tx) => {
      setPendingTx({
        retry: () => {
          unblock();
          tx.retry();
        },
      });
    });
    return unblock;
  }, [isDirty]);

  const handleCancel = useCallback(() => setPendingTx(null), []);

  const handleDiscard = useCallback(() => {
    const tx = pendingTx;
    if (!tx) return;
    setPendingTx(null);
    tx.retry();
  }, [pendingTx]);

  const handleSaveAndLeave = useCallback(async () => {
    const tx = pendingTx;
    if (!tx) return;
    await save();
    setPendingTx(null);
    tx.retry();
  }, [pendingTx, save]);

  return (
    <Modal show={pendingTx !== null} onClose={handleCancel} disableOverlayClick size="md">
      <Modal.Header
        title={t`Несохранённые изменения`}
        subtitle={t`У вас есть несохранённые правки в этом шаблоне. Что хотите сделать?`}
      />
      <Modal.Footer justify="between">
        <UDButton variant="secondary" type="button" onClick={handleCancel} disabled={isSaving}>
          <Trans>Отмена</Trans>
        </UDButton>
        <div className="flex gap-3">
          <UDButton variant="secondary" type="button" onClick={handleDiscard} disabled={isSaving}>
            <Trans>Не сохранять</Trans>
          </UDButton>
          <UDButton variant="primary" type="button" onClick={handleSaveAndLeave} loading={isSaving} disabled={isSaving}>
            <Trans>Сохранить и выйти</Trans>
          </UDButton>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default UnsavedChangesGuard;
